import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, CardHeader, ConfirmDialog,
  EmptyState, Field, Input, PageHeader, Skeleton,
} from '../components/ui'
import { useInstances } from '../hooks/useInstances'
import {
  useAddImage, useDeleteImage, useImageCatalog, useRollbackImage,
  useSetDefaultImage, useSwapImage, useUpdateImage,
} from '../hooks/useImages'
import { extractApiError } from './users/format'
import type { ImageUpstreamCheck, PlatformImage } from '../api/client'

/**
 * Did the proxy give up before the server did?
 *
 * A swap takes a pre-op backup, pulls up to 3.3 GB, recreates the container and
 * then polls health for up to 60s. Behind Cloudflare (~100s cap) that routinely
 * outlives the connection: the gateway answers the browser while the swap keeps
 * running server-side. Keeping the request synchronous is deliberate — but
 * reporting the timeout as a failure is wrong twice over. The operation is not
 * over, and an admin told "that did not work" clicks again, which is a second
 * swap on top of the first.
 *
 * Detected from the gateway status codes a proxy synthesises (502/503/504, and
 * Cloudflare's own 524) plus axios's client-side timeout codes. A plain 4xx/5xx
 * from our own API still reads as the failure it is.
 */
function isProxyTimeout(e: unknown): boolean {
  const err = e as { code?: string; response?: { status?: number } } | undefined
  const status = err?.response?.status
  return status === 502 || status === 503 || status === 504 || status === 524
    || err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT'
}

export default function AdminImagesPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const catalog = useImageCatalog()
  const instances = useInstances()
  const setDefault = useSetDefaultImage()
  const remove = useDeleteImage()
  const swap = useSwapImage()
  const rollback = useRollbackImage()
  const add = useAddImage()
  const update = useUpdateImage()

  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<PlatformImage | null>(null)
  const [newRef, setNewRef] = useState('')
  const [newNotes, setNewNotes] = useState('')

  const fail = (e: unknown) => {
    setNotice(null)
    setError(extractApiError(e) ?? t('images.actionFailed'))
  }

  // A swap or rollback that the proxy cut short is still running: say so, and
  // refresh what we can so the page catches up once it lands.
  const failLongOp = (e: unknown) => {
    if (!isProxyTimeout(e)) {
      fail(e)
      return
    }
    setError(null)
    setNotice(t('images.stillRunning'))
    qc.invalidateQueries({ queryKey: ['admin', 'images'] })
    qc.invalidateQueries({ queryKey: ['instances'] })
  }

  const addImage = () => {
    const ref = newRef.trim()
    if (!ref) return
    add.mutate({ ref, notes: newNotes.trim() }, {
      onError: fail,
      onSuccess: () => {
        setError(null)
        setNewRef('')
        setNewNotes('')
      },
    })
  }

  if (catalog.isLoading) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
        <Skeleton className="h-6 w-40 mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }
  if (catalog.isError) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
        <EmptyState title={extractApiError(catalog.error) ?? t('images.loadFailed')} />
      </div>
    )
  }

  const images = catalog.data?.images ?? []
  const upstream = catalog.data?.upstream ?? []

  // One card per kind+series, in catalogue order.
  const groups: { key: string; kind: string; series: string; rows: PlatformImage[] }[] = []
  for (const img of images) {
    const key = `${img.kind}/${img.series}`
    let g = groups.find((x) => x.key === key)
    if (!g) {
      g = { key, kind: img.kind, series: img.series, rows: [] }
      groups.push(g)
    }
    g.rows.push(img)
  }

  // PostgreSQL official tags never carry the date suffix the upstream checker
  // looks for, so a missing entry here for kind=postgres is permanent and
  // normal — not a stale check or a fault to surface. We simply render
  // nothing for that group when `up` is undefined; no empty-state, no warning.
  const upstreamFor = (kind: string, series: string): ImageUpstreamCheck | undefined =>
    upstream.find((u) => u.kind === kind && u.series === series)

  const known = (ref: string) => images.some((i) => i.ref === ref)

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
      <PageHeader title={t('images.title')} description={t('images.description')} />

      {error && <Alert tone="danger" className="mb-4">{error}</Alert>}
      {notice && <Alert tone="accent" className="mb-4">{notice}</Alert>}

      {/* The only way to pin a PostgreSQL image through the product: official
          postgres tags carry no date suffix, so the upstream checker never
          produces a finding for them and its "add to catalogue" button never
          appears for that kind. It is also how a series gets a second Odoo row
          before the checker has run. */}
      <Card className="mb-4">
        <CardHeader title={t('images.addTitle')} description={t('images.addHint')} />
        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <Field label={t('images.refLabel')}>
            {(p) => (
              <Input
                {...p}
                value={newRef}
                onChange={(e) => setNewRef(e.target.value)}
                placeholder="odoo:19.0-20260908"
              />
            )}
          </Field>
          <Field label={t('images.notesLabel')}>
            {(p) => (
              <Input {...p} value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
            )}
          </Field>
        </div>
        <div className="mt-3">
          <Button loading={add.isPending} disabled={!newRef.trim()} onClick={addImage}>
            {t('images.addToCatalog')}
          </Button>
        </div>
      </Card>

      {groups.map((g) => {
        const up = upstreamFor(g.kind, g.series)
        return (
          <Card key={g.key} className="mb-4" padded={false}>
            <div className="p-5 pb-0">
              <CardHeader title={`${g.kind} ${g.series}`} />
            </div>
            {up && !known(up.latest_ref) && (
              <Alert tone="accent" className="mx-5 mt-3 mb-3">
                {t('images.upstreamNewer', { ref: up.latest_ref })}{' '}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={add.isPending}
                  onClick={() => add.mutate({ ref: up.latest_ref, notes: '' }, { onError: fail })}
                >
                  {t('images.addToCatalog')}
                </Button>
              </Alert>
            )}
            <table className="w-full text-sm">
              <tbody>
                {g.rows.map((img) => (
                  <tr key={img.id} className="border-t border-gray-200">
                    <td className="px-5 py-3 font-mono text-xs">{img.ref}</td>
                    <td className="px-5 py-3">
                      {img.is_default && <Badge tone="accent">{t('images.default')}</Badge>}
                      {img.rolling && <Badge tone="warning">{t('images.rolling')}</Badge>}
                      {!img.enabled && <Badge tone="neutral">{t('images.disabled')}</Badge>}
                    </td>
                    {/* The date the upstream build was published, kept out of
                        toLocaleDateString on purpose: the ISO prefix reads the
                        same on every machine, which is what a version pin
                        wants. */}
                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">
                      {img.released_at
                        ? t('images.releasedAt', { date: img.released_at.slice(0, 10) })
                        : null}
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      <span>{img.instance_count}</span> {t('images.instanceCount')}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {!img.is_default && img.enabled && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={setDefault.isPending}
                          onClick={() => setDefault.mutate(img.id, { onError: fail })}
                        >
                          {t('images.makeDefault')}
                        </Button>
                      )}
                      {/* Withdrawing a row is how the rolling seed stops being
                          offered to new instances without deleting the entry
                          the instances already on it are explained by. The
                          default row cannot be withdrawn — the series would
                          have nothing left to resolve. */}
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={img.is_default}
                        title={img.is_default ? t('images.toggleDefaultBlocked') : undefined}
                        loading={update.isPending}
                        onClick={() =>
                          update.mutate(
                            { id: img.id, enabled: !img.enabled, notes: img.notes },
                            { onError: fail },
                          )
                        }
                      >
                        {img.enabled ? t('images.disable') : t('images.enable')}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={img.instance_count > 0}
                        title={img.instance_count > 0
                          ? t('images.deleteInUse', { count: img.instance_count })
                          : undefined}
                        onClick={() => setConfirmDelete(img)}
                      >
                        {t('common.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      })}

      <Card padded={false}>
        <div className="p-5 pb-0">
          <CardHeader title={t('images.instancesTitle')} />
        </div>
        {instances.isError ? (
          <EmptyState title={extractApiError(instances.error) ?? t('images.instancesLoadFailed')} />
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {(instances.data ?? []).map((inst) => (
                <tr key={inst.id} className="border-t border-gray-200">
                  <td className="px-5 py-3">{inst.subdomain}</td>
                  <td className="px-5 py-3 font-mono text-xs">
                    {t('images.currentImage', { ref: inst.odoo_image_ref })}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {images
                      .filter((i) => i.kind === 'odoo' && i.series === inst.odoo_version
                        && i.ref !== inst.odoo_image_ref && i.enabled)
                      .map((i) => (
                        <Button
                          key={i.id}
                          size="sm"
                          variant="secondary"
                          loading={swap.isPending}
                          onClick={() =>
                            swap.mutate({ instanceId: inst.id, imageId: i.id }, { onError: failLongOp })
                          }
                        >
                          {t('images.upgrade')} → {i.ref}
                        </Button>
                      ))}
                    {inst.previous_odoo_image_ref && (
                      <>
                        <Button
                          size="sm"
                          variant="danger"
                          loading={rollback.isPending}
                          onClick={() => rollback.mutate(inst.id, { onError: failLongOp })}
                        >
                          {t('images.rollback')}
                        </Button>
                        {/* Rollback restores the image binary, never the data —
                            the backend's pre-op backup is the only real undo.
                            Kept visible next to the action, not in a tooltip. */}
                        <p className="text-xs text-gray-500 mt-1">
                          {t('images.rollbackDataWarning')}
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t('images.confirmDeleteTitle')}
        message={confirmDelete?.ref ?? ''}
        busy={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id, { onError: fail })
          setConfirmDelete(null)
        }}
      />
    </div>
  )
}
